/*
  File: src/contexts/AdIntegration.js
  Description:
    - Implements AdMob banner and interstitial strategy for free-tier users
    - Consent flow (iOS ATT + Android/iOS GDPR via UMP)
    - Pre-load interstitial for zero lag
    - Banner positioned in safe area, below header, above bottom nav
    - show/hide/refresh banner on navigation and pagination
    - showInterstitial on first recipe open per session
*/
/* ANDROID 
    ca-app-pub-7703978361783181~7282003264
    your interstitial Ad Unit ID //TEST: ca-app-pub-3940256099942544/1033173712   LIVE: ca-app-pub-7703978361783181/3568637893
    your banner Ad Unit ID  //TEST: ca-app-pub-3940256099942544/6300978111  LIVE:ca-app-pub-7703978361783181/9866167526
*/
/* IOS
    ca-app-pub-7703978361783181~9519269894
    banner: ca-app-pub-7703978361783181/6252293942
    interstital: ca-app-pub-7703978361783181/7960977347
*/

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { AdMob, AdmobConsentStatus } from '@capacitor-community/admob';
import { Capacitor } from '@capacitor/core';
import { useSubscription } from './SubscriptionContext';

// Ad Unit IDs (use your TEST or LIVE IDs as appropriate)
const ADS = {
  banner: {
    ios: 'ca-app-pub-7703978361783181/6252293942',       // LIVE
    android: 'ca-app-pub-7703978361783181/9866167526',    // LIVE
  },
  interstitial: {
    ios: 'ca-app-pub-7703978361783181/7960977347',       // LIVE
    android: 'ca-app-pub-7703978361783181/3568637893',    // LIVE
  },
};

const AdContext = createContext();
export const useAds = () => {
  const ctx = useContext(AdContext);
  if (!ctx) throw new Error('useAds must be used within AdProvider');
  return ctx;
};

export const AdProvider = ({ children }) => {
  const { isSubscribed } = useSubscription();
  const [adsInitialized, setAdsInitialized] = useState(false);
  const [interstitialReady, setInterstitialReady] = useState(false);
  const [hasShownInterstitial, setHasShownInterstitial] = useState(false);
  const lastInterstitialAt = useRef(0);

  // Platform-specific IDs
  const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'
  const bannerID = platform === 'ios' ? ADS.banner.ios : ADS.banner.android;
  const interstitialID = platform === 'ios' ? ADS.interstitial.ios : ADS.interstitial.android;

  // Consent + tracking (iOS ATT + Android/iOS GDPR)
  const collectConsent = async () => {
    const [trackingInfo, consentInfo] = await Promise.all([
      AdMob.trackingAuthorizationStatus(),
      AdMob.requestConsentInfo(),
    ]);
    if (trackingInfo.status === 'notDetermined') {
      await AdMob.requestTrackingAuthorization();
    }
    const { status: authStatus } = await AdMob.trackingAuthorizationStatus();
    if (
      authStatus === 'authorized' &&
      consentInfo.isConsentFormAvailable &&
      consentInfo.status === AdmobConsentStatus.REQUIRED
    ) {
      await AdMob.showConsentForm();
    }
  };

  // Initialize AdMob & preload interstitial
  const initAds = async () => {
    await collectConsent();
    await AdMob.initialize();
    setAdsInitialized(true);
    await AdMob.prepareInterstitial({ adId: interstitialID });
    setInterstitialReady(true);
  };

  // Start initialization for free-tier users
  useEffect(() => {
    if (!isSubscribed) {
      initAds();
    }
  }, [isSubscribed]);

  // Banner configuration
  const bannerOpts = {
    adId: bannerID,
    adSize: 'ADAPTIVE_BANNER',
    position: 'BOTTOM_CENTER',
    margin: 0,
  };

  const showBanner = () => {
    if (isSubscribed || !adsInitialized) return;
    //AdMob.showBanner(bannerOpts);
    AdMob.showBanner(bannerOpts)
    .catch(() => AdMob.resumeBanner(bannerOpts))
    .catch(console.error)
  };
  const removeBanner = () => {
    if (!adsInitialized) return;
    AdMob.removeBanner();
  };
  const refreshBanner = () => {
    removeBanner();
    showBanner();
  };

  // Interstitial logic (once-per-session cap, 2min spacing)
  const showInterstitial = async () => {
    if (isSubscribed || !adsInitialized || !interstitialReady || hasShownInterstitial) return;
    const now = Date.now();
    if (now - lastInterstitialAt.current < 120000) return; // 2-minute guard

    await AdMob.showInterstitial({ adId: interstitialID });
    lastInterstitialAt.current = now;
    setHasShownInterstitial(true);
    await AdMob.prepareInterstitial({ adId: interstitialID });
  };

  return (
    <AdContext.Provider value={{ showBanner, removeBanner, refreshBanner, showInterstitial }}>
        {children}
    </AdContext.Provider>
  );
};